import React, { useState, useEffect, useRef } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import FollowupEmailSettings from '../components/FollowupEmailSettings';
import QRCode from 'react-qr-code';

// Global error handler for debugging
window.addEventListener('error', (event) => {
  if (event.error?.message?.includes('Objects are not valid as a React child')) {
    console.error('🚨 React Child Object Error Details:', {
      message: event.error.message,
      stack: event.error.stack,
      filename: event.filename,
      lineno: event.lineno
    });
  }
});
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  Upload, 
  Settings, 
  Building, 
  Phone, 
  Globe, 
  MapPin, 
  Mail, 
  Palette, 
  MessageSquare,
  Lock,
  Save,
  Eye,
  EyeOff,
  UserPlus,
  Users,
  Edit,
  Trash2,
  X,
  Sparkles,
  Copy,
  Check,
  Smartphone,
  ExternalLink,
  Zap,
  QrCode,
  Download,
  Printer,
  Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';
import { useToast } from '../hooks/use-toast';
import { BaaSettingsCard } from '../components/baa/BaaSettingsCard';

const PracticeSettingsPage = () => {
  const navigate = useNavigate();
  const { user, practice, logout, refreshPractice } = useAuth();
  const { toast } = useToast();
  const qrCodeRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  
  // SmileDesign AI Link state
  const [smileDesignLink, setSmileDesignLink] = useState({
    shortLink: null,
    fullUrl: null,
    slug: null,
    loading: true,
    generating: false,
    copied: false,
    copiedType: null
  });

  // Send SmileDesign link modal state
  const [sendLinkModal, setSendLinkModal] = useState(null); // 'email' or 'sms'
  const [sendLinkTo, setSendLinkTo] = useState('');
  const [sendingLink, setSendingLink] = useState(false);

  const handleSendSmileDesignLink = async () => {
    if (!sendLinkTo.trim()) return;
    setSendingLink(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dentalToken');
      const res = await fetch(`${backendUrl}/api/practice/send-smile-design-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type: sendLinkModal, recipient: sendLinkTo.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: sendLinkModal === 'email' ? 'Email sent!' : 'Text sent!', description: data.message });
        setSendLinkModal(null);
        setSendLinkTo('');
      } else {
        toast({ title: 'Failed to send', description: data.detail || 'Something went wrong', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSendingLink(false);
    }
  };

  const [formData, setFormData] = useState({
    // Practice Info
    name: '',
    phone: '',
    website: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    officeHours: '',
    emergencyContact: '',
    
    // Branding
    logo: '',
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    
    // Settings
    autoEmailReminders: true,
    customBranding: false,
    
    // Password Change
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [dentists, setDentists] = useState([]);
  const [specialtiesList, setSpecialtiesList] = useState([]);
  const [newDentist, setNewDentist] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cellPhone: '',
    licenseNumber: '',
    specialties: []
  });

  // Debug function to safely log objects
  const debugLog = (message, obj) => {
    try {
      console.log(message, JSON.stringify(obj, null, 2));
    } catch (e) {
      console.log(message, obj);
    }
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    console.log('🔢 Formatting phone number:', value);
    
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    console.log('📱 Cleaned phone number:', phoneNumber);
    
    // Don't format if less than 4 digits
    if (phoneNumber.length < 4) {
      console.log('📱 Too short, returning as-is');
      return phoneNumber;
    }
    
    let formatted;
    // Format as (123) 123-1234
    if (phoneNumber.length <= 6) {
      formatted = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      formatted = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
    
    console.log('📱 Formatted result:', formatted);
    return formatted;
  };

  // Monitor all state changes that might cause re-renders
  useEffect(() => {
    console.log('🔍 newDentist state changed:', newDentist);
  }, [newDentist]);

  useEffect(() => {
    console.log('🔍 dentists state changed:', dentists);
  }, [dentists]);
  const [showAddDentist, setShowAddDentist] = useState(false);

  useEffect(() => {
    if (practice) {
      setFormData({
        name: practice.name || '',
        phone: practice.phone || '',
        website: practice.website || '',
        street: practice.address?.street || '',
        city: practice.address?.city || '',
        state: practice.address?.state || '',
        zipCode: practice.address?.zipCode || '',
        officeHours: practice.officeHours || '',
        emergencyContact: practice.emergencyContact || '',
        logo: practice.branding?.logo || '',
        primaryColor: practice.branding?.primaryColor || '#2563eb',
        secondaryColor: practice.branding?.secondaryColor || '#1e40af',
        autoEmailReminders: practice.settings?.autoEmailReminders !== false,
        customBranding: practice.settings?.customBranding === true,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Load dentists for this practice
      loadDentists();
      loadSpecialties();
    }
  }, [practice]);

  // Load SmileDesign AI link on mount and when practice changes
  useEffect(() => {
    const loadLink = async () => {
      try {
        setSmileDesignLink(prev => ({ ...prev, loading: true }));
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('dentalToken');
        
        if (!token) {
          setSmileDesignLink(prev => ({ ...prev, loading: false }));
          return;
        }
        
        const response = await fetch(`${backendUrl}/api/practice/smile-design-link`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setSmileDesignLink(prev => ({
              ...prev,
              shortLink: data.data.shortLink,
              fullUrl: data.data.fullUrl,
              slug: data.data.slug,
              loading: false
            }));
          } else {
            setSmileDesignLink(prev => ({ ...prev, loading: false }));
          }
        } else {
          setSmileDesignLink(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Failed to load SmileDesign link:', error);
        setSmileDesignLink(prev => ({ ...prev, loading: false }));
      }
    };
    
    // Call immediately on mount
    loadLink();
  }, []); // Empty dependency - run once on mount

  // Load SmileDesign AI Link (keep for manual refresh if needed)
  const loadSmileDesignLink = async () => {
    try {
      setSmileDesignLink(prev => ({ ...prev, loading: true }));
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dentalToken');
      
      const response = await fetch(`${backendUrl}/api/practice/smile-design-link`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSmileDesignLink(prev => ({
            ...prev,
            shortLink: data.data.shortLink,
            fullUrl: data.data.fullUrl,
            slug: data.data.slug,
            loading: false
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load SmileDesign link:', error);
      setSmileDesignLink(prev => ({ ...prev, loading: false }));
    }
  };

  // Generate SmileDesign AI Short Link
  const generateSmileDesignShortLink = async () => {
    try {
      setSmileDesignLink(prev => ({ ...prev, generating: true }));
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dentalToken');
      
      const response = await fetch(`${backendUrl}/api/practice/smile-design-link/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSmileDesignLink(prev => ({
            ...prev,
            shortLink: data.shortLink,
            generating: false
          }));
          toast({
            title: "Short Link Created!",
            description: "Your mobile-friendly link is ready to share with patients.",
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to generate short link. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Failed to generate short link:', error);
      toast({
        title: "Error",
        description: "Failed to generate short link. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSmileDesignLink(prev => ({ ...prev, generating: false }));
    }
  };

  // Copy SmileDesign link to clipboard
  const copySmileDesignLink = async (link, type) => {
    try {
      await navigator.clipboard.writeText(link);
      setSmileDesignLink(prev => ({ ...prev, copied: true, copiedType: type }));
      setTimeout(() => {
        setSmileDesignLink(prev => ({ ...prev, copied: false, copiedType: null }));
      }, 2000);
      toast({
        title: "Copied!",
        description: "Link copied to clipboard.",
      });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSmileDesignLink(prev => ({ ...prev, copied: true, copiedType: type }));
      setTimeout(() => {
        setSmileDesignLink(prev => ({ ...prev, copied: false, copiedType: null }));
      }, 2000);
    }
  };

  // Download QR Code as PNG
  const downloadQRCode = () => {
    const svg = qrCodeRef.current?.querySelector('svg');
    if (!svg) {
      toast({
        title: "Error",
        description: "QR code not found. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    // Create canvas from SVG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    // Set canvas size with padding for a nice border
    const size = 300;
    const padding = 20;
    canvas.width = size + (padding * 2);
    canvas.height = size + (padding * 2);
    
    img.onload = () => {
      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code centered with padding
      ctx.drawImage(img, padding, padding, size, size);
      
      // Download
      const link = document.createElement('a');
      link.download = `smiledesign-qr-${practice?.name?.replace(/\s+/g, '-').toLowerCase() || 'practice'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({
        title: "Downloaded!",
        description: "QR code saved as PNG image.",
      });
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const loadDentists = async () => {
    try {
      const response = await practiceApi.getDentists();
      setDentists(response.data || []);
    } catch (error) {
      console.error('Failed to load dentists:', error);
      // If API doesn't exist yet, initialize with empty array
      setDentists([]);
    }
  };

  const loadSpecialties = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dentalToken');
      const response = await fetch(`${backendUrl}/api/referrals/specialties`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSpecialtiesList(data.specialties || []);
    } catch (error) {
      console.error('Failed to load specialties:', error);
    }
  };

  const handleAddDentist = async () => {
    // Frontend validation
    if (!newDentist.firstName || !newDentist.lastName || !newDentist.email) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields (First Name, Last Name, Email)",
        variant: "destructive",
      });
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newDentist.email)) {
      setFieldErrors({email: "Please enter a valid email address"});
      return;
    }

    setSaving(true);
    try {
      if (newDentist.id) {
        // Update existing dentist
        await practiceApi.updateDentist(newDentist.id, {
          firstName: newDentist.firstName,
          lastName: newDentist.lastName,
          email: newDentist.email,
          phone: newDentist.phone,
          cellPhone: newDentist.cellPhone,
          licenseNumber: newDentist.licenseNumber,
          specialties: newDentist.specialties
        });
        toast({
          title: "Success",
          description: "Dentist updated successfully!",
        });
      } else {
        // Add new dentist
        await practiceApi.addDentist(newDentist);
        toast({
          title: "Success",
          description: "Dentist added successfully!",
        });
      }
      
      setNewDentist({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        cellPhone: '',
        licenseNumber: '',
        specialties: []
      });
      
      // Clear any field errors on successful submission
      setFieldErrors({});
      
      setShowAddDentist(false);
      loadDentists(); // Reload the list
    } catch (error) {
      console.error('🚨 Add dentist error caught:', error);
      console.error('🚨 Error response data:', error.response?.data);
      
      let errorMessage = "Failed to save dentist";
      
      let hasFieldErrors = false;
      const newFieldErrors = {};
      
      try {
        // Handle different error response formats
        if (error.response?.data) {
          const errorData = error.response.data;
          console.log('🔍 Processing error data:', errorData);
          
          // Handle Pydantic validation errors (array format)
          if (Array.isArray(errorData)) {
            console.log('📝 Array format validation error');
            errorData.forEach(err => {
              console.log('🔍 Processing error:', err);
              if (typeof err === 'object' && err.loc && err.msg) {
                const fieldName = err.loc[err.loc.length - 1]; // Get the field name
                console.log('🎯 Field name detected:', fieldName);
                if (fieldName === 'email') {
                  newFieldErrors.email = err.msg;
                  hasFieldErrors = true;
                  console.log('✅ Email field error set:', err.msg);
                } else {
                  errorMessage = err.msg;
                  console.log('📋 General error message set:', err.msg);
                }
              }
            });
            if (!hasFieldErrors) {
              errorMessage = errorData.map(err => 
                typeof err === 'object' && err.msg ? err.msg : String(err)
              ).join(', ');
            }
          }
          // Handle FastAPI validation error with detail containing array
          else if (errorData.detail && Array.isArray(errorData.detail)) {
            console.log('📝 Detail array format validation error');
            errorData.detail.forEach(err => {
              console.log('🔍 Processing detail error:', err);
              if (typeof err === 'object' && err.loc && err.msg) {
                const fieldName = err.loc[err.loc.length - 1]; // Get the field name
                console.log('🎯 Detail field name detected:', fieldName);
                if (fieldName === 'email') {
                  newFieldErrors.email = err.msg;
                  hasFieldErrors = true;
                  console.log('✅ Detail email field error set:', err.msg);
                } else {
                  errorMessage = err.msg;
                  console.log('📋 Detail general error message set:', err.msg);
                }
              }
            });
            if (!hasFieldErrors) {
              errorMessage = errorData.detail.map(err => 
                typeof err === 'object' && err.msg ? err.msg : String(err)
              ).join(', ');
            }
          }
          // Handle standard error responses
          else if (typeof errorData.detail === 'string') {
            console.log('📝 String detail error');
            errorMessage = errorData.detail;
          }
          // Handle any other object error responses - convert to string
          else if (typeof errorData === 'object') {
            console.log('📝 Object error - converting to string');
            errorMessage = `Validation error: ${JSON.stringify(errorData)}`;
          }
          else {
            console.log('📝 Other error type');
            errorMessage = String(errorData);
          }
        }
        // Fallback for network errors
        else if (error.message) {
          errorMessage = error.message;
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
        errorMessage = "An unexpected error occurred";
      }
      
      // Ensure the errorMessage is always a string
      if (typeof errorMessage !== 'string') {
        console.warn('⚠️ Error message was not a string:', errorMessage);
        errorMessage = String(errorMessage);
      }
      
      console.log('✅ Final error processing:', { 
        hasFieldErrors, 
        newFieldErrors, 
        errorMessage,
        action: newDentist.id ? 'UPDATE' : 'ADD'
      });
      
      // Set field-specific errors
      setFieldErrors(newFieldErrors);
      console.log('🔧 Set fieldErrors state to:', newFieldErrors);
      
      // Only show toast for general errors, not field-specific ones
      if (!hasFieldErrors) {
        console.log('📢 Showing toast error since no field errors detected');
        toast({
          title: "Error", 
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        console.log('🎯 Field errors detected, not showing toast:', newFieldErrors);
        console.log('🚫 Suppressing toast notification');
      }
      
      // Clear any form error
      setFormError(null);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDentist = async (dentistId) => {
    if (!confirm('Are you sure you want to remove this dentist?')) return;

    try {
      await practiceApi.removeDentist(dentistId);
      toast({
        title: "Success",
        description: "Dentist removed successfully",
      });
      loadDentists();
    } catch (error) {
      toast({
        title: "Error", 
        description: "Failed to remove dentist",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, file.size, file.type);
      
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast({
          title: "File Too Large",
          description: "Please select an image under 2MB",
          variant: "destructive",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('File loaded, data URL length:', e.target.result.length);
        setFormData({
          ...formData,
          logo: e.target.result
        });
        
        toast({
          title: "Logo Uploaded",
          description: "Logo uploaded successfully! Click 'Save Branding' to save changes.",
          variant: "default",
        });
      };
      
      reader.onerror = (e) => {
        console.error('FileReader error:', e);
        toast({
          title: "Upload Failed",
          description: "Failed to read the selected file.",
          variant: "destructive",
        });
      };
      
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    
    try {
      let updateData = {};
      
      if (section === 'practice') {
        updateData = {
          name: formData.name,
          phone: formData.phone,
          website: formData.website,
          officeHours: formData.officeHours,
          emergencyContact: formData.emergencyContact,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode
          }
        };
      } else if (section === 'branding') {
        updateData = {
          branding: {
            logo: formData.logo,
            primaryColor: formData.primaryColor,
            secondaryColor: formData.secondaryColor,
            welcomeMessage: formData.welcomeMessage
          }
        };
        console.log('Sending branding update:', updateData);
      } else if (section === 'password') {
        if (!formData.newPassword) {
          toast({
            title: "Password Required",
            description: "Please enter a new password",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }
        
        if (formData.newPassword !== formData.confirmPassword) {
          toast({
            title: "Password Mismatch",
            description: "Passwords do not match",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        updateData = {
          newPassword: formData.newPassword
        };
      }

      const response = await practiceApi.updatePractice(updateData);
      
      if (response.success) {
        // Refresh practice data to ensure the UI reflects the changes
        await refreshPractice();
        
        toast({
          title: "Settings Updated",
          description: "Your practice settings have been saved successfully",
          variant: "default",
        });
        
        if (section === 'password') {
          setFormData({
            ...formData,
            newPassword: '',
            confirmPassword: ''
          });
        }
      }
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to update settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Dashboard</span>
              </Button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <Settings className="h-5 w-5 text-gray-600" />
                <h1 className="text-xl font-semibold text-gray-900">Practice Settings</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          
          {/* Practice Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  <span>Practice Information</span>
                </CardTitle>
                <Button
                  onClick={() => {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    window.open(`${backendUrl}/api/view-quick-start-guide`, '_blank');
                  }}
                  variant="outline"
                  className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-4 w-4" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Quick Start Guide</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Practice Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Practice Name"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        const formattedPhone = formatPhoneNumber(e.target.value);
                        handleInputChange({...e, target: {...e.target, value: formattedPhone, name: 'phone'}});
                      }}
                      className="pl-10"
                      placeholder="(555) 123-4567"
                      maxLength={14}
                    />
                  </div>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder="www.yourpractice.com"
                    />
                  </div>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Office Hours</label>
                  <Input
                    name="officeHours"
                    value={formData.officeHours || ''}
                    onChange={handleInputChange}
                    placeholder="Mon-Fri: 8:00 AM - 5:00 PM, Sat: 9:00 AM - 2:00 PM"
                  />
                  <p className="text-xs text-gray-500">These will appear at the bottom of PDF care guides</p>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Emergency Contact Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      name="emergencyContact"
                      value={formData.emergencyContact || ''}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <p className="text-xs text-gray-500">After-hours emergency contact for urgent situations</p>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  <span>Practice Address</span>
                </h3>
                
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Street Address</label>
                    <Input
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">City</label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">State</label>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">ZIP Code</label>
                      <Input
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleSave('practice')}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Practice Info'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <BaaSettingsCard />

          {/* Section 1: Smile Design AI — Digital Sharing */}
          <Card className="border-l-4 border-l-blue-500 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid="smile-design-digital-section">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Send className="h-5 w-5 text-blue-600" />
                <span>SmileDesign AI — Send to Patient</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <p className="text-sm text-gray-600">
                  Send your SmileDesign AI link directly to a patient via email or text message. 
                  They'll receive a one-tap link to open your personalized Smile Design tool on their device.
                </p>
              </div>

              {smileDesignLink.loading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  <span className="ml-2 text-gray-500">Loading your link...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Short Link */}
                  {smileDesignLink.shortLink && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <Smartphone className="w-4 h-4" />
                        Short Link (Easy to Share)
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                          <code className="text-green-700 font-medium">{smileDesignLink.shortLink}</code>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => copySmileDesignLink(smileDesignLink.shortLink, 'short')}
                          className={smileDesignLink.copied && smileDesignLink.copiedType === 'short' ? 'bg-green-50 border-green-500' : ''}
                        >
                          {smileDesignLink.copied && smileDesignLink.copiedType === 'short' ? (
                            <><Check className="w-4 h-4 mr-2 text-green-600" /> Copied!</>
                          ) : (
                            <><Copy className="w-4 h-4 mr-2" /> Copy</>
                          )}
                        </Button>
                        <Button variant="ghost" onClick={() => window.open(smileDesignLink.shortLink, '_blank')} title="Preview in new tab">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Full Link */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                      <Globe className="w-4 h-4" />
                      Full Link
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 overflow-x-auto">
                        <code className="text-gray-700 text-sm whitespace-nowrap">
                          {`https://app.practcom.com/public-smile-design?practice=${smileDesignLink.slug}`}
                        </code>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => copySmileDesignLink(`https://app.practcom.com/public-smile-design?practice=${smileDesignLink.slug}`, 'full')}
                        className={smileDesignLink.copied && smileDesignLink.copiedType === 'full' ? 'bg-green-50 border-green-500' : ''}
                      >
                        {smileDesignLink.copied && smileDesignLink.copiedType === 'full' ? (
                          <><Check className="w-4 h-4 mr-2 text-green-600" /> Copied!</>
                        ) : (
                          <><Copy className="w-4 h-4 mr-2" /> Copy</>
                        )}
                      </Button>
                      <Button variant="ghost" onClick={() => window.open(`https://app.practcom.com/public-smile-design?practice=${smileDesignLink.slug}`, '_blank')} title="Preview in new tab">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Email / Text Buttons */}
                  <div className="border-t border-blue-200 pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Send directly to a patient:</p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => setSendLinkModal('email')}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="dacn-email-smile-link"
                      >
                        <Mail className="w-4 h-4 mr-2" /> Email to Patient
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setSendLinkModal('sms')}
                        data-testid="dacn-text-smile-link"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" /> Text to Patient
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section 2: Printed Materials — QR Code & Physical Assets */}
          <Card className="border-l-4 border-l-amber-500 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="smile-design-printed-section">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Printer className="h-5 w-5 text-amber-600" />
                <span>Printed Materials — QR Code</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <p className="text-sm text-gray-600">
                  Download or print your QR code for in-office use — place it on brochures, front-desk signs, 
                  business cards, or treatment room displays so patients can scan and access SmileDesign AI on their own phone.
                </p>
              </div>

              {smileDesignLink.loading ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center">
                  <div className="w-[180px] h-[180px] bg-gray-100 animate-pulse rounded-lg flex items-center justify-center">
                    <span className="text-gray-400">Loading QR Code...</span>
                  </div>
                </div>
              ) : smileDesignLink.slug ? (
                <div className="space-y-4">
                  {/* QR Code Display */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center">
                    <div ref={qrCodeRef} className="bg-white p-4 rounded-lg shadow-sm">
                      <QRCode 
                        value={`https://app.practcom.com/public-smile-design?practice=${smileDesignLink.slug}`} 
                        size={180}
                        level="H"
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                      />
                    </div>
                    <p className="text-sm text-gray-600 mt-3 text-center">
                      Scan to access SmileDesign AI
                    </p>
                    <p className="text-xs text-gray-400 mt-1 text-center max-w-[250px] break-all">
                      app.practcom.com
                    </p>
                    <Button
                      onClick={downloadQRCode}
                      className="mt-4 bg-amber-600 hover:bg-amber-700"
                      data-testid="download-qr-png"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download QR Code (PNG)
                    </Button>
                  </div>

                  {/* Reception Desk Sign */}
                  <div className="border-t border-amber-200 pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                      Ready-to-Print Reception Sign
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      Generate an 8.5" x 11" sign with your practice name, QR code, and patient instructions
                    </p>
                    <Button
                      onClick={() => {
                        const logoParam = practice?.logo ? `&logo=${encodeURIComponent(practice.logo)}` : '';
                        const signUrl = `/smile-design-sign?practice=${encodeURIComponent(practice?.name || 'Your Practice')}&url=${encodeURIComponent(`https://app.practcom.com/public-smile-design?practice=${smileDesignLink.slug}`)}${logoParam}`;
                        window.open(signUrl, '_blank');
                      }}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                      disabled={smileDesignLink.loading || !smileDesignLink.slug}
                      data-testid="generate-reception-sign"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {smileDesignLink.loading ? 'Loading...' : 'Generate Reception Desk Sign'}
                    </Button>
                  </div>

                  {/* Tips */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-medium text-blue-900 text-sm">Pro Tips:</p>
                    <ul className="text-sm text-blue-800 mt-1 space-y-1">
                      <li>• Print the QR code on business cards or appointment reminders</li>
                      <li>• Display the QR code on a tablet or poster in your waiting room</li>
                      <li>• Add the QR code to patient intake forms or brochures</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center">
                  <p className="text-gray-500 mb-3">Unable to load QR code</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const loadLink = async () => {
                        setSmileDesignLink(prev => ({ ...prev, loading: true }));
                        try {
                          const backendUrl = process.env.REACT_APP_BACKEND_URL;
                          const token = localStorage.getItem('dentalToken');
                          const response = await fetch(`${backendUrl}/api/practice/smile-design-link`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            if (data.success) {
                              setSmileDesignLink(prev => ({
                                ...prev,
                                shortLink: data.data.shortLink,
                                fullUrl: data.data.fullUrl,
                                slug: data.data.slug,
                                loading: false
                              }));
                            } else {
                              setSmileDesignLink(prev => ({ ...prev, loading: false }));
                            }
                          } else {
                            setSmileDesignLink(prev => ({ ...prev, loading: false }));
                          }
                        } catch (error) {
                          setSmileDesignLink(prev => ({ ...prev, loading: false }));
                        }
                      };
                      loadLink();
                    }}
                  >
                    Retry Loading
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Palette className="h-5 w-5 text-purple-600" />
                <span>Practice Branding</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Logo Upload */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Practice Logo</h3>
                <div className="flex items-center space-x-4">
                  {formData.logo && (
                    <div className="w-20 h-20 border border-gray-300 rounded-lg overflow-hidden">
                      <img 
                        src={formData.logo} 
                        alt="Practice Logo" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      type="button"
                      onClick={() => document.getElementById('logo-upload').click()}
                      className="cursor-pointer"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {formData.logo ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    <p className="text-sm text-gray-600 mt-1">PNG, JPG up to 2MB. This appears on all patient instructions.</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Brand Colors */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Brand Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Primary Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        name="primaryColor"
                        value={formData.primaryColor}
                        onChange={handleInputChange}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <Input
                        name="primaryColor"
                        value={formData.primaryColor}
                        onChange={handleInputChange}
                        className="font-mono text-sm"
                        placeholder="#2563eb"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Secondary Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        name="secondaryColor"
                        value={formData.secondaryColor}
                        onChange={handleInputChange}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <Input
                        name="secondaryColor"
                        value={formData.secondaryColor}
                        onChange={handleInputChange}
                        className="font-mono text-sm"
                        placeholder="#1e40af"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Welcome Message */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-purple-600" />
                  <span>Welcome Message</span>
                </h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Custom message for patients</label>
                  <textarea
                    name="welcomeMessage"
                    value={formData.welcomeMessage}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Welcome to our practice's post-operative care portal"
                  />
                  <p className="text-sm text-gray-600">This message appears when patients access their post-op instructions.</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleSave('branding')}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Branding'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="h-5 w-5 text-green-600" />
                <span>Account Security</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="pl-10 pr-10"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleSave('password')}
                  disabled={saving || !formData.newPassword}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Follow-up Email Settings */}
          <FollowupEmailSettings />

          {/* Quick Dentist Management - Simplified */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-indigo-600" />
                  <span>Dentist Management</span>
                </div>
                <Button 
                  onClick={() => setShowAddDentist(!showAddDentist)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {showAddDentist ? 'Close' : 'Add Dentist'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Current Dentists List */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Practice Dentists</h3>
                
                {dentists.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No dentists added yet</p>
                    <p className="text-sm">Add dentists to your practice to assign procedures</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dentists.map((dentist) => (
                      <div key={dentist.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">
                                Dr. {dentist.firstName} {dentist.lastName}
                              </h4>
                              <p className="text-sm text-gray-600">{dentist.email}</p>
                              {dentist.phone && (
                                <p className="text-sm text-gray-500">{dentist.phone}</p>
                              )}
                              {dentist.licenseNumber && (
                                <p className="text-sm text-gray-500">License: {dentist.licenseNumber}</p>
                              )}
                              {dentist.specialties && dentist.specialties.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {dentist.specialties.map((specialty, index) => (
                                    <span key={index} className="inline-block bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded">
                                      {specialty}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setNewDentist({
                                ...dentist,
                                specialties: dentist.specialties || []
                              });
                              setShowAddDentist(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveDentist(dentist.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add/Edit Dentist Form */}
              {showAddDentist && (
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {newDentist.id ? 'Edit Dentist' : 'Add New Dentist'}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowAddDentist(false);
                        setNewDentist({
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: '',
                          cellPhone: '',
                          licenseNumber: '',
                          specialties: []
                        });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">First Name *</label>
                      <Input
                        value={newDentist.firstName}
                        onChange={(e) => setNewDentist({...newDentist, firstName: e.target.value})}
                        placeholder="Enter first name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Last Name *</label>
                      <Input
                        value={newDentist.lastName}
                        onChange={(e) => setNewDentist({...newDentist, lastName: e.target.value})}
                        placeholder="Enter last name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Email Address *</label>
                      <Input
                        type="text"
                        value={newDentist.email}
                        onChange={(e) => {
                          const newEmail = e.target.value;
                          console.log('Email onChange:', newEmail);
                          
                          // Clear field error when user starts typing
                          if (fieldErrors.email) {
                            setFieldErrors({...fieldErrors, email: null});
                          }
                          
                          const updatedDentist = {...newDentist, email: newEmail};
                          debugLog('Setting newDentist to:', updatedDentist);
                          
                          setNewDentist(updatedDentist);
                        }}
                        onBlur={(e) => {
                          console.log('Email onBlur:', e.target.value);
                        }}
                        placeholder="Enter email address"
                        autoComplete="email"
                        noValidate
                        className={fieldErrors.email ? "border-red-500 focus:border-red-500" : ""}
                      />
                      {fieldErrors.email && (
                        <p className="text-red-500 text-sm mt-1 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {fieldErrors.email}
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Phone Number</label>
                      <Input
                        value={newDentist.phone}
                        onChange={(e) => {
                          const formattedPhone = formatPhoneNumber(e.target.value);
                          setNewDentist({...newDentist, phone: formattedPhone});
                        }}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Cell Phone <span className="text-xs font-normal text-gray-500">(for post-treatment reply alerts)</span>
                      </label>
                      <Input
                        value={newDentist.cellPhone || ''}
                        onChange={(e) => {
                          const formattedPhone = formatPhoneNumber(e.target.value);
                          setNewDentist({...newDentist, cellPhone: formattedPhone});
                        }}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                        data-testid="dentist-cellphone-input"
                      />
                      <p className="text-xs text-gray-500">
                        Optional. When set, you'll get an SMS copy whenever a patient replies to a post-treatment check-in.
                      </p>
                    </div>
                    
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">License Number</label>
                      <Input
                        value={newDentist.licenseNumber}
                        onChange={(e) => setNewDentist({...newDentist, licenseNumber: e.target.value})}
                        placeholder="Enter license number"
                      />
                    </div>
                    
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">Specialties</label>
                      <div className="border rounded-lg p-3 bg-white">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {specialtiesList.map((specialty) => (
                            <label
                              key={specialty}
                              className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={newDentist.specialties?.includes(specialty) || false}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  let updatedSpecialties;
                                  
                                  if (isChecked) {
                                    // Add specialty
                                    updatedSpecialties = [...(newDentist.specialties || []), specialty];
                                  } else {
                                    // Remove specialty
                                    updatedSpecialties = newDentist.specialties.filter(s => s !== specialty);
                                  }
                                  
                                  setNewDentist({
                                    ...newDentist,
                                    specialties: updatedSpecialties
                                  });
                                }}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                              <span className="text-sm text-gray-700">{specialty}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {newDentist.specialties && newDentist.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <p className="text-sm text-gray-600 w-full">Selected specialties:</p>
                          {newDentist.specialties.map((specialty) => (
                            <span
                              key={specialty}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                            >
                              {specialty}
                              <button
                                type="button"
                                onClick={() => {
                                  setNewDentist({
                                    ...newDentist,
                                    specialties: newDentist.specialties.filter(s => s !== specialty)
                                  });
                                }}
                                className="ml-2 text-indigo-600 hover:text-indigo-800"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-gray-500">Select one or more specialties for this dentist</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddDentist(false);
                        setNewDentist({
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: '',
                          cellPhone: '',
                          licenseNumber: '',
                          specialties: []
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddDentist}
                      disabled={saving}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : (newDentist.id ? 'Update Dentist' : 'Add Dentist')}
                    </Button>
                  </div>
                </div>
              )}

            </CardContent>
          </Card>

          {/* Billing & Support */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Mail className="h-5 w-5 text-blue-600" />
                <span>Billing & Support</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  <strong>Billing Questions:</strong> For subscription changes, payment issues, or cancellations, please email{' '}
                  <a href="mailto:admin@practcom.com" className="text-blue-600 underline">
                    admin@practcom.com
                  </a>
                </AlertDescription>
              </Alert>
              
              <div className="mt-4 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  <p className="mb-2">
                    <strong>Current Subscription:</strong>{' '}
                    {(() => {
                      const t = practice?.subscription?.tier;
                      const labels = {
                        essential: 'Essential',
                        professional: 'Professional',
                        enterprise: 'Enterprise',
                        grandfather: "Founder's Plan",
                      };
                      return labels[t] || practice?.subscription?.type || 'Essential';
                    })()}
                  </p>
                  <p className="mb-2 flex items-center">
                    <strong className="mr-2">Status:</strong>
                    {(() => {
                      const status = practice?.subscription?.status;
                      console.log('Subscription status:', status, 'Type:', typeof status);
                      
                      if (status === 'active') {
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✅ Active
                          </span>
                        );
                      } else if (status === 'trial') {
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            🎯 Trial
                          </span>
                        );
                      } else if (status === 'cancelled') {
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            ⛔ Cancelled
                          </span>
                        );
                      } else if (status === 'beta') {
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            🚀 Beta
                          </span>
                        );
                      } else {
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            ⚠️ {status || 'Inactive'}
                          </span>
                        );
                      }
                    })()}
                    {practice?.subscription?.status === 'trial' && practice?.subscription?.trialEndDate && (
                      <span className="ml-2 text-xs text-gray-500">
                        (Expires: {new Date(practice.subscription.trialEndDate).toLocaleDateString()})
                      </span>
                    )}
                  </p>
                  <p>
                    <strong>Need Help?</strong> Contact <a href="mailto:support@practcom.com" className="text-blue-600 underline">support@practcom.com</a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
      
      {/* Field-level validation errors now display inline */}

      {/* Send SmileDesign Link Modal */}
      {sendLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setSendLinkModal(null); setSendLinkTo(''); }}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()} data-testid="send-smile-link-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              {sendLinkModal === 'email' ? 'Email SmileDesign AI Link' : 'Text SmileDesign AI Link'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Send your SmileDesign AI link to a patient so they can see their future smile.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {sendLinkModal === 'email' ? 'Patient Email' : 'Patient Phone Number'}
                </label>
                <Input
                  type={sendLinkModal === 'email' ? 'email' : 'tel'}
                  value={sendLinkTo}
                  onChange={(e) => setSendLinkTo(e.target.value)}
                  placeholder={sendLinkModal === 'email' ? 'patient@example.com' : '(555) 123-4567'}
                  data-testid="send-smile-link-input"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => { setSendLinkModal(null); setSendLinkTo(''); }} data-testid="send-smile-link-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleSendSmileDesignLink}
                  disabled={sendingLink || !sendLinkTo.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="send-smile-link-submit"
                >
                  {sendingLink ? 'Sending...' : (
                    <><Send className="w-4 h-4 mr-2" /> Send</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default PracticeSettingsPage;